import {CommonModule} from '@angular/common';
import {Component, Inject} from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import {AvistaCredentials, PaymentProvider} from '@gatewee/types/provider';
import {TranslateModule, TranslateService} from '@ngx-translate/core';
import {TuiAlertService, TuiDialogContext} from '@taiga-ui/core';
import {POLYMORPHEUS_CONTEXT} from '@taiga-ui/polymorpheus';
import {GateweeButtonComponent} from '../../gatewee-button/gatewee-button.component';
import {Observable} from 'rxjs';
import {GateweeIconComponent} from '../../gatewee-icon/gatewee-icon.component';

interface DialogData {
  id: number;
  submitCredentialsFn: (
    provider: PaymentProvider,
    payload: unknown,
  ) => Observable<void>;
  provider: PaymentProvider;
}

@Component({
  selector: 'app-avista-credentials',
  templateUrl: './avista-credentials.component.html',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TranslateModule,
    GateweeButtonComponent,
    GateweeIconComponent,
  ],
})
export class AvistaCredentialsComponent {
  form: FormGroup;
  submitting = false;
  showSecret = false;
  id!: number;
  submitCredentialsFn!: (
    provider: PaymentProvider,
    payload: unknown,
  ) => Observable<void>;
  provider!: PaymentProvider;

  mtlsCertFileName = '';

  constructor(
    private fb: FormBuilder,
    private alerts: TuiAlertService,
    private translate: TranslateService,
    @Inject(POLYMORPHEUS_CONTEXT)
    private readonly context: TuiDialogContext<boolean, DialogData>,
  ) {
    this.id = this.context.data.id;
    this.submitCredentialsFn = this.context.data.submitCredentialsFn;
    this.provider = this.context.data.provider;
    this.form = this.fb.group({
      sandbox: [false],
      clientId: ['', [Validators.required, Validators.minLength(3)]],
      clientSecret: ['', [Validators.required, Validators.minLength(3)]],
      mtlsCert: [null as string | null, Validators.required],
    });
  }

  async onFileChange(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) {
      this.form.get('mtlsCert')?.setValue(null);
      this.mtlsCertFileName = '';
      return;
    }

    try {
      const base64 = await this.fileToBase64(file);
      const clean = base64.split(',').pop() ?? base64;
      this.form.get('mtlsCert')?.setValue(clean);
      this.form.get('mtlsCert')?.markAsDirty();
      this.mtlsCertFileName = file.name;
    } catch (e) {
      console.error('Error reading file', e);
      this.form.get('mtlsCert')?.setErrors({fileRead: true});
    }
  }

  private fileToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onerror = () => reject(reader.error);
      reader.onload = () => resolve(reader.result as string);
      reader.readAsDataURL(file);
    });
  }

  onSubmit(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    this.submitting = true;

    const formValue = this.form.value;
    const payload: AvistaCredentials = {
      sandbox: formValue.sandbox,
      clientId: formValue.clientId,
      clientSecret: formValue.clientSecret,
      mtlsCert: formValue.mtlsCert,
    };

    let handledSuccess = false;
    const handleSuccess = () => {
      if (handledSuccess) return;
      handledSuccess = true;
      this.alerts
        .open(
          this.translate.instant(
            'features.integrations.credentials.success.message',
          ),
          {
            label: this.translate.instant(
              'features.integrations.credentials.success.title',
            ),
            appearance: 'success',
          },
        )
        .subscribe();
      this.context.completeWith(true);
    };

    this.submitCredentialsFn(this.provider, payload).subscribe({
      next: () => handleSuccess(),
      complete: () => handleSuccess(),
      error: (error: Error) => {
        console.error('Error creating credentials:', error);
        this.alerts
          .open(
            this.translate.instant(
              'features.integrations.credentials.error.message',
            ),
            {
              label: this.translate.instant(
                'features.integrations.credentials.error.title',
              ),
              appearance: 'error',
            },
          )
          .subscribe();
        this.submitting = false;
      },
    });
  }

  close(): void {
    this.context.completeWith(false);
  }
}
